import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Globe, Brain, Wrench, Zap, Bot, Sparkles } from 'lucide-react';

const AI = [
  {
    icon: Brain,
    title: 'AI Strategy & Transformation',
    price: 'From $1,500',
    desc: "Not sure where AI fits in your business? I map your workflows, find the highest-ROI opportunities, and hand you a concrete plan — not a slide deck.",
    bullets: ['Workflow analysis & AI opportunity mapping', 'Build vs. buy recommendation', 'Roadmap with timeline and real cost estimates'],
  },
  {
    icon: Bot,
    title: 'Custom AI Agent Development',
    price: 'Project-based',
    desc: 'Agentic AI that does real work — lead qualification, customer follow-up, document processing, and scheduling. Multi-step AI agents built for your business, not a generic template.',
    bullets: ['Multi-step reasoning agents & tool use', 'CRM, calendar, and email integration', 'Production deployment with monitoring'],
  },
  {
    icon: Zap,
    title: 'LLM Integration & Automation',
    price: 'Project-based',
    desc: 'Add AI to your existing tools — your website, inbox, or CRM. Summarization, classification, drafting, extraction. Ship in weeks, not months.',
    bullets: ['RAG pipelines over your own data', 'Claude, GPT-4, Gemini, and local models', 'Prompt engineering & output evaluation'],
  },
];

const BUILD = [
  {
    icon: Globe,
    title: 'Small Business Websites',
    price: '$800 flat',
    desc: 'A modern, mobile-first site built for your business in days. Hosting, contact form, Google integration, and a year of small updates included.',
    bullets: ['Custom design — not a template', 'See it live before you pay', 'Hosted and maintained for you'],
  },
  {
    icon: Sparkles,
    title: 'Embedded Generative AI',
    price: 'Project-based',
    desc: 'AI features that actually move the needle inside your product — chat, search, summaries, agents. From scoping to production deployment.',
    bullets: ['LLM integration & prompt engineering', 'RAG, agents, evaluation', 'Cost & latency optimization'],
  },
  {
    icon: Wrench,
    title: 'Web Dev Consulting',
    price: 'Hourly or retainer',
    desc: 'Architecture review, code audits, and pair-building with your existing team. Pragmatic guidance for small teams that need to ship.',
    bullets: ['Stack & architecture reviews', 'Performance & DX cleanup', 'Hands-on with your team'],
  },
];

function ServiceCard({ item }) {
  return (
    <div className="group relative glass rounded-2xl p-7 hover:border-accent/40 transition-colors h-full">
      <div className="absolute -inset-px rounded-2xl bg-gradient-brand opacity-0 group-hover:opacity-10 transition-opacity pointer-events-none" />
      <div className="relative">
        <div className="w-11 h-11 rounded-xl bg-gradient-brand-soft flex items-center justify-center mb-5 border border-border">
          <item.icon size={20} className="text-accent-2" />
        </div>
        <h3 className="font-display font-semibold text-xl text-text mb-1">{item.title}</h3>
        <p className="text-xs text-accent-2 font-medium mb-4">{item.price}</p>
        <p className="text-text-muted text-sm mb-5 leading-relaxed">{item.desc}</p>
        <ul className="space-y-2">
          {item.bullets.map((b) => (
            <li key={b} className="text-sm text-text flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-accent-2 flex-shrink-0" />
              {b}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function WhatIDo() {
  const [tab, setTab] = useState('ai');

  return (
    <section id="services" className="relative py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl mb-10">
          <p className="text-sm text-accent-2 font-medium mb-3 uppercase tracking-wider">What I do</p>
          <h2 className="font-display font-bold text-3xl md:text-4xl text-text mb-4">
            Agentic AI. Custom websites. Real automation.
          </h2>
          <p className="text-text-muted text-lg">
            From AI strategy and custom AI agents to a $800 website and done-for-you automation — I help small businesses compete like they have a full team.
          </p>
        </div>

        <div className="flex gap-2 mb-10">
          <button
            onClick={() => setTab('ai')}
            className={`px-5 py-2 rounded-full text-sm font-medium border transition-all ${
              tab === 'ai'
                ? 'bg-gradient-brand text-white border-transparent shadow-glow'
                : 'glass text-text-muted hover:text-text hover:border-accent/40'
            }`}
          >
            Agentic AI
          </button>
          <button
            onClick={() => setTab('build')}
            className={`px-5 py-2 rounded-full text-sm font-medium border transition-all ${
              tab === 'build'
                ? 'bg-gradient-brand text-white border-transparent shadow-glow'
                : 'glass text-text-muted hover:text-text hover:border-accent/40'
            }`}
          >
            Build
          </button>
        </div>

        {tab === 'ai' && (
          <motion.div
            key="ai"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="grid md:grid-cols-3 gap-6"
          >
            {AI.map((item) => (
              <ServiceCard key={item.title} item={item} />
            ))}
            <div className="md:col-span-3 text-center pt-2">
              <p className="text-sm text-text-muted">
                Not sure where to start?{' '}
                <a href="#contact" className="text-accent-2 hover:underline">Book a free 30-min AI strategy call</a>
                {' '}— I'll tell you exactly what's worth building and what isn't.
              </p>
            </div>
          </motion.div>
        )}

        {tab === 'build' && (
          <motion.div
            key="build"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="grid md:grid-cols-3 gap-6"
          >
            {BUILD.map((item) => (
              <ServiceCard key={item.title} item={item} />
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}

export default WhatIDo;
